/* ==========================================================================
   Mobile sticky add-to-cart bar (snippets/xenon-sticky-atc.liquid).

   Shown while the page's own Sepete Ekle (#ProductSubmitButton-<section>) is
   off screen, above or below, and hidden while it is in view. The bar's button
   clicks that real button, so every rule the product form already applies —
   variant, quantity, Birlikte Al ticks, the loading guard, the cart drawer —
   applies here too, and nothing about adding to cart is reimplemented.
   ========================================================================== */
(() => {
  if (customElements.get('xenon-sticky-atc')) return;

  const PHONE = window.matchMedia('(max-width: 767.98px)');

  class XenonStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      // The set builder pins its own summary bar, with its own button, to the
      // same edge. Two bars would stack.
      if (document.querySelector('xenon-set-builder')) return;

      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-xsatc-button]');
      this.label = this.querySelector('[data-xsatc-label]');
      this.price = this.querySelector('[data-xsatc-price]');
      this.mainVisible = null;

      this.button.addEventListener('click', this.onClick.bind(this));

      this.io = new IntersectionObserver((entries) => {
        this.mainVisible = entries[entries.length - 1].isIntersecting;
        this.update();
      });

      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(this.sync.bind(this));
      };

      // The info column is re-rendered piecemeal on a variant or colour change
      // (product-info.js, product-color-variants.js): the price row, the label
      // and even the submit button itself are swapped for fresh nodes. Watching
      // the column and re-reading it is what keeps the bar in step.
      const info = document.getElementById(`ProductInfo-${this.sectionId}`);
      if (info) {
        this.mo = new MutationObserver(this.schedule);
        this.mo.observe(info, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
      }
      PHONE.addEventListener('change', this.schedule);

      // A colour swatch swaps the whole page to a sibling product without a
      // reload (product-color-variants.js): new title, new gallery, new form.
      // The price and the button follow through sync(); the name and the
      // picture are re-read here from what the page now shows.
      this.onSibling = this.syncIdentity.bind(this);
      document.addEventListener('color-sibling:changed', this.onSibling);

      this.hidden = false;
      this.inert = true;
      this.sync();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.schedule) PHONE.removeEventListener('change', this.schedule);
      if (this.onSibling) document.removeEventListener('color-sibling:changed', this.onSibling);
      document.body.classList.remove('xsatc-visible');
    }

    // Name and picture of the product the page switched to, from its .js
    // endpoint: the gallery's first image in the DOM is not reliably the
    // featured one (slider order, video posters), and the swap hands over a
    // URL, not a product. One small request, and only on a colour change.
    async syncIdentity(event) {
      this.schedule();

      const url = event && event.detail && event.detail.url;
      if (!url) return;
      const ticket = (this.identityTicket = (this.identityTicket || 0) + 1);

      try {
        const target = new URL(url, window.location.origin);
        const response = await fetch(`${target.pathname.replace(/\/$/, '')}.js`);
        if (!response.ok || ticket !== this.identityTicket) return;
        const product = await response.json();
        if (ticket !== this.identityTicket) return;

        const title = this.querySelector('.xsatc__title');
        if (title && product.title) {
          const name = product.title.split('|')[0].trim();
          title.textContent = name;
          this.setAttribute('aria-label', `${name} — hızlı satın alma`);
        }

        const variantId = Number(target.searchParams.get('variant'));
        const variant = variantId && product.variants.find((item) => item.id === variantId);
        const src = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image;
        const image = this.querySelector('.xsatc__image');
        if (image && src) {
          const sized = new URL(src, window.location.origin);
          sized.searchParams.set('width', '132');
          sized.searchParams.set('height', '132');
          sized.searchParams.set('crop', 'center');
          image.removeAttribute('srcset');
          image.src = sized.toString();
        }
      } catch (error) {
        // The bar keeps the previous name and picture; price and button are
        // already right, because sync() read them off the page.
      }
    }

    get mainButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    // Re-reads the page: which button to watch, what it says, whether it can
    // be pressed, and the price beside it. Writes only what changed, so the
    // shipping countdown ticking inside the column costs a comparison a second.
    sync() {
      const main = this.mainButton;
      if (main !== this.watched) {
        if (this.watched) this.io.unobserve(this.watched);
        this.watched = main;
        this.mainVisible = null;
        if (main) this.io.observe(main);
      }

      if (main) {
        const text = (main.querySelector('.btn__text') || main).textContent.trim();
        if (text && this.label.textContent !== text) this.label.textContent = text;

        // Only a real add in flight reads as busy. The page's button also
        // carries aria-disabled while a ticked Birlikte Al card still needs its
        // colour ("Renk Seçiniz"), and there a press must go through: the
        // page's own submit handler answers it by scrolling to that card.
        const busy = main.classList.contains('btn--loading');
        if (busy !== (this.button.getAttribute('aria-disabled') === 'true')) {
          if (busy) this.button.setAttribute('aria-disabled', 'true');
          else this.button.removeAttribute('aria-disabled');
        }
        this.button.classList.toggle('btn--loading', main.classList.contains('btn--loading'));
      }

      this.syncPrice();
      this.update();
    }

    // The price as the page shows it right now: the selling price, and the
    // struck-through one beside it. xenon-product-price.liquid prints it for
    // most products; a product with a cart-only discount
    // (custom.cart_discount_amount) gets product-dynamic-cart-discount.liquid
    // instead, whose headline is the in-cart price.
    syncPrice() {
      const scope = `#ProductInfo-${this.sectionId}`;
      const row = document.querySelector(`${scope} .xprice__values`);
      const deal = !row && document.querySelector(`${scope} .product-cart-discount-price__deal strong`);
      const current = row ? row.querySelector('.xprice__sale, .xprice__current') : deal;
      const compare = row
        ? row.querySelector('.xprice__compare')
        : deal && document.querySelector(`${scope} .product-cart-discount-price__old`);
      const key = `${current ? current.textContent.trim() : ''}|${compare ? compare.textContent.trim() : ''}`;
      if (key === this.priceKey) return;
      this.priceKey = key;

      this.price.replaceChildren();
      if (!current) return;

      const now = document.createElement('span');
      now.textContent = current.textContent.trim();
      this.price.append(now);

      if (compare) {
        const was = document.createElement('s');
        was.className = 'xsatc__compare';
        was.textContent = compare.textContent.trim();
        this.price.append(was);
      }
    }

    update() {
      const main = this.watched;
      // No layout box means the button is display:none (a template that hides
      // it), and a disabled one is sold out or unavailable: neither has
      // anything for the bar to offer.
      const usable = PHONE.matches && main && main.getClientRects().length > 0 && !main.disabled;
      this.setVisible(Boolean(usable && this.mainVisible === false));
    }

    setVisible(on) {
      if (on === this.classList.contains('is-visible')) return;
      this.classList.toggle('is-visible', on);
      this.inert = !on;
      document.body.classList.toggle('xsatc-visible', on);
      if (on) document.documentElement.style.setProperty('--xsatc-height', `${this.offsetHeight}px`);
    }

    onClick() {
      const main = this.mainButton;
      // aria-disabled is left to the page: ProductForm and complementary-look.js
      // both refuse a second submit while one is in flight, and a pending
      // Birlikte Al colour is answered by scrolling to it.
      if (!main || main.disabled) return;

      const done = new AbortController();
      const once = { signal: done.signal };
      setTimeout(() => done.abort(), 15000);

      // The cart drawer gives focus back, when it closes, to the button that
      // opened it — the page's own, a screen or more away — and focus()
      // scrolls the page there. It is pointed at this bar's button instead;
      // the bar is fixed, so nothing moves. 'toggle' fires synchronously once
      // the drawer has recorded that button (theme.js ModalComponent).
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.addEventListener(
          'toggle',
          () => {
            if (drawer.open && drawer.activeElement === main) drawer.activeElement = this.button;
            done.abort();
          },
          once
        );
      }

      // A refused add (a stock limit, say) is explained under the page's own
      // button, which is off screen while the bar is up. Bring it into view;
      // the page's button comes with it, so the bar steps away rather than
      // cover the message. Birlikte Al scrolls its own box the same way.
      document.addEventListener(
        'product-ajax:error',
        () => {
          done.abort();
          const box = main.form && main.form.querySelector('.product-form__error-message:not([hidden])');
          (box || main).scrollIntoView({ block: 'center', behavior: 'smooth' });
        },
        once
      );

      main.click();
    }
  }

  customElements.define('xenon-sticky-atc', XenonStickyAtc);
})();
